import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Send,
  Search,
  X,
  School as SchoolIcon,
  CheckCircle2,
  Lightbulb,
  Instagram,
  ChevronDown,
  Check,
} from "lucide-react";
import PageShell from "@/components/PageShell";
import Button from "@/components/Button";
import FormField from "@/components/FormField";
import { api } from "@/lib/api";

const ENTRY_TYPES = [
  {
    value: "Blog",
    label: "Travel Story (Blog)",
    desc: "Write your travel story in detail. There is no word limit.",
  },
  {
    value: "Reel",
    label: "Travel Reel",
    desc: "Record a short video explaining your travel experience.",
  },
  {
    value: "Video",
    label: "Travel Video",
    desc: "Record a longer video explaining your travel experience.",
  },
  {
    value: "Photo Story",
    label: "Photo Story / Artwork",
    desc: "Share photographs or artwork that tell your travel story.",
  },
];

export default function SubmitEntry() {
  const navigate = useNavigate();
  const [schools, setSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [schoolSearch, setSchoolSearch] = useState("");
  const [form, setForm] = useState({
    student_full_name: "",
    school_id: "",
    grade: "",
    section: "",
    parent_contact: "",
    student_phone: "",
    student_email: "",
    entry_type: "Blog",
    title: "",
    story_text: "",
    external_link: "",
    consent: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const selectedSchool = useMemo(() => schools.find((s) => s.id === form.school_id), [schools, form.school_id]);

  useEffect(() => {
    api
      .approvedSchools()
      .then((list) => setSchools(list))
      .catch(() => setSchools([]))
      .finally(() => setLoadingSchools(false));
  }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const filteredSchools = useMemo(() => {
    const q = schoolSearch.trim().toLowerCase();
    if (!q) return schools;
    return schools.filter(
      (s) => s.school_name.toLowerCase().includes(q) || (s.city || "").toLowerCase().includes(q)
    );
  }, [schools, schoolSearch]);

  const validate = () => {
    const e = {};
    if (form.student_full_name.trim().length < 2) e.student_full_name = "Full name is required";
    if (!form.school_id) e.school_id = "Select your school";
    if (!form.grade.trim()) e.grade = "Grade is required";
    if (!form.section.trim()) e.section = "Section is required";
    if (form.parent_contact.trim().length < 7) e.parent_contact = "Enter a valid contact number";
    if (form.student_email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.student_email))
      e.student_email = "Enter a valid email";
    if (form.title.trim().length < 2) e.title = "Title is required";
    if (!form.story_text.trim() && !form.external_link.trim())
      e.content = "Add a story or paste a link to your work";
    if (!form.consent) e.consent = "You must confirm this is your original work";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev?.preventDefault?.();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.submitEntry({
        student_full_name: form.student_full_name,
        school_id: form.school_id,
        grade: form.grade,
        section: form.section,
        parent_contact: form.parent_contact,
        student_phone: form.student_phone || null,
        student_email: form.student_email || null,
        entry_type: form.entry_type,
        title: form.title,
        story_text: form.story_text || null,
        external_link: form.external_link || null,
        consent: form.consent,
      });
      navigate("/success", { replace: true });
    } catch (err) {
      toast.error(`Submission failed: ${err.message || "Please try again."}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell testid="submit-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-14 h-14 rounded-full bg-sprouts-blush flex items-center justify-center shrink-0">
            <Send size={26} className="text-sprouts-secondary" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-sprouts-text">Submit Your Entry</h1>
            <p className="text-sm font-bold text-sprouts-text mt-1.5" data-testid="sub-audience-note">
              This form is to be filled by Students / Parents only.
            </p>
            <p className="text-sm text-sprouts-muted mt-1.5 leading-relaxed">
              Share your travel story, reel or photos from a <span className="font-semibold text-sprouts-text">school trip with your classmates and teachers</span> — personal or family holiday stories are less preferred. Only students from approved schools can submit. If your school isn't listed, ask your teacher to register it first.
            </p>
          </div>
        </div>

        {/* Important Dates */}
        <div
          className="bg-sprouts-lavender/60 border border-sprouts-border rounded-2xl p-4 sm:p-5 mb-6"
          data-testid="sub-important-dates"
        >
          <p className="text-[11px] font-extrabold tracking-[0.2em] uppercase text-sprouts-primary mb-2">
            Important Dates
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <p className="text-xs font-bold text-sprouts-muted">Last date for entry submission</p>
              <p className="text-base font-black text-sprouts-text">10 September 2026</p>
            </div>
            <div>
              <p className="text-xs font-bold text-sprouts-muted">Winners will be announced by</p>
              <p className="text-base font-black text-sprouts-text">20 September 2026</p>
            </div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white rounded-2xl p-5 sm:p-7 border border-sprouts-border shadow-card"
        >
          <FormField
            label="Student Full Name"
            required
            placeholder="Aarav Patel"
            value={form.student_full_name}
            onChange={(e) => set("student_full_name", e.target.value)}
            error={errors.student_full_name}
            data-testid="sub-name"
          />

          {/* School picker */}
          <label className="block text-[13px] font-bold text-sprouts-text mb-1.5">
            Select School Name <span className="text-sprouts-secondary">*</span>
          </label>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            data-testid="sub-school-picker"
            className={`w-full flex items-center justify-between rounded-xl border bg-white px-4 py-3 text-sm font-semibold mb-4 transition-colors ${
              errors.school_id ? "border-sprouts-secondary" : "border-sprouts-border"
            }`}
          >
            <span className={selectedSchool ? "text-sprouts-text" : "text-sprouts-muted font-medium"}>
              {loadingSchools
                ? "Loading approved schools..."
                : selectedSchool
                ? selectedSchool.school_name
                : schools.length === 0
                ? "No approved schools yet — check back soon"
                : "Tap to choose your school"}
            </span>
            <ChevronDown size={18} className="text-sprouts-muted shrink-0" />
          </button>
          {errors.school_id && (
            <p className="text-xs text-sprouts-secondary font-semibold -mt-3 mb-3">{errors.school_id}</p>
          )}

          <FormField
            label="City"
            placeholder="Auto-filled from school"
            value={selectedSchool ? `${selectedSchool.city}${selectedSchool.state ? ", " + selectedSchool.state : ""}` : ""}
            readOnly
            data-testid="sub-city"
          />

          <div className="grid sm:grid-cols-2 gap-x-4">
            <FormField
              label="Grade"
              required
              inputMode="numeric"
              placeholder="e.g. 7"
              value={form.grade}
              onChange={(e) => set("grade", e.target.value)}
              error={errors.grade}
              data-testid="sub-grade"
            />
            <FormField
              label="Section"
              required
              placeholder="A"
              value={form.section}
              onChange={(e) => set("section", e.target.value)}
              error={errors.section}
              data-testid="sub-section"
            />
          </div>

          <FormField
            label="Parent / Teacher Contact"
            required
            type="tel"
            placeholder="+91 9876543210"
            value={form.parent_contact}
            onChange={(e) => set("parent_contact", e.target.value)}
            error={errors.parent_contact}
            data-testid="sub-parent-contact"
          />
          <FormField
            label="Student Contact Number (optional)"
            type="tel"
            placeholder="+91 9876543210"
            value={form.student_phone}
            onChange={(e) => set("student_phone", e.target.value)}
            data-testid="sub-student-phone"
          />
          <FormField
            label="Student Email (optional)"
            type="email"
            autoCapitalize="none"
            placeholder="you@example.com"
            value={form.student_email}
            onChange={(e) => set("student_email", e.target.value)}
            error={errors.student_email}
            data-testid="sub-email"
          />

          <label className="block text-[13px] font-bold text-sprouts-text mb-2">
            Type of Entry <span className="text-sprouts-secondary">*</span>
          </label>
          <div className="flex flex-wrap gap-2 mb-3">
            {ENTRY_TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => set("entry_type", t.value)}
                data-testid={`sub-type-${t.value.toLowerCase().replace(" ", "-")}`}
                className={`px-4 py-2.5 rounded-full text-sm font-bold border transition-colors ${
                  form.entry_type === t.value
                    ? "bg-sprouts-primary text-white border-sprouts-primary"
                    : "bg-slate-100 text-sprouts-text border-sprouts-border hover:bg-slate-200"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <ul className="mb-4 space-y-1.5 text-xs text-sprouts-muted leading-relaxed" data-testid="sub-type-descriptions">
            {ENTRY_TYPES.filter((t) => t.value === form.entry_type).map((t) => (
              <li key={t.value} className="flex gap-2">
                <span className="font-extrabold text-sprouts-text shrink-0">{t.label} —</span>
                <span>{t.desc}</span>
              </li>
            ))}
          </ul>

          <FormField
            label="Title of Submission"
            required
            placeholder="My amazing day in Jaipur"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            error={errors.title}
            data-testid="sub-title"
          />
          <FormField
            label="Story / Description"
            multiline
            rows={5}
            placeholder="Tell us about your trip. There is no word limit, so feel free to share your experience, memorable moments, learnings and everything that made your journey special."
            value={form.story_text}
            onChange={(e) => set("story_text", e.target.value)}
            data-testid="sub-story"
          />

          <label className="block text-[13px] font-bold text-sprouts-text mb-2">Share Your Work</label>
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-4 mb-2.5">
            <Lightbulb size={18} className="text-sprouts-warning shrink-0 mt-0.5" />
            <p className="text-xs text-sprouts-text leading-relaxed">
              Paste a link to your blog, reel, video or photo story. If you're using{" "}
              <span className="font-black">Google Drive</span>, please set the link to{" "}
              <span className="font-black">"Anyone with the link can view"</span> so we can open your entry. You can
              also share an Instagram Reel or YouTube link — whatever works best for you.
            </p>
          </div>
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-4 mb-3">
            <Instagram size={18} className="text-sprouts-secondary shrink-0 mt-0.5" />
            <p className="text-xs text-sprouts-text leading-relaxed">
              Prefer Instagram? Follow us at <span className="font-black">@sproutsedutour</span> and feel free to DM
              your entry link to us there.
            </p>
          </div>
          <FormField
            placeholder="Paste your Google Drive / Instagram / YouTube link"
            autoCapitalize="none"
            value={form.external_link}
            onChange={(e) => set("external_link", e.target.value)}
            data-testid="sub-link"
          />
          {errors.content && (
            <p className="-mt-2 mb-3 text-xs text-sprouts-secondary font-semibold">{errors.content}</p>
          )}

          {/* Consent */}
          <button
            type="button"
            onClick={() => set("consent", !form.consent)}
            data-testid="sub-consent"
            className="w-full flex items-center gap-3 bg-sprouts-lavender rounded-xl p-4 text-left mt-2"
          >
            <span
              className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 ${
                form.consent ? "bg-sprouts-primary border-sprouts-primary" : "bg-white border-sprouts-primary"
              }`}
            >
              {form.consent && <Check size={14} className="text-white" strokeWidth={3} />}
            </span>
            <span className="text-sm font-semibold text-sprouts-text">I confirm this is my original work.</span>
          </button>
          {errors.consent && (
            <p className="mt-1 text-xs text-sprouts-secondary font-semibold">{errors.consent}</p>
          )}

          <div className="mt-6">
            <Button type="submit" variant="secondary" loading={submitting} data-testid="sub-submit" fullWidth>
              {submitting ? "Submitting..." : "Submit My Story"}
            </Button>
          </div>
        </form>
      </div>

      {/* School picker modal */}
      {pickerOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-6"
          onClick={() => setPickerOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-3xl p-5 sm:p-6 max-h-[85vh] flex flex-col"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-extrabold text-sprouts-text">Select Your School</h3>
              <button
                type="button"
                onClick={() => setPickerOpen(false)}
                data-testid="picker-close"
                className="p-1.5 rounded-md hover:bg-slate-100"
                aria-label="Close"
              >
                <X size={22} />
              </button>
            </div>
            <div className="relative mb-4">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sprouts-muted" />
              <input
                type="text"
                value={schoolSearch}
                onChange={(e) => setSchoolSearch(e.target.value)}
                placeholder="Search by name or city"
                data-testid="picker-search"
                className="w-full rounded-xl border border-sprouts-border bg-white pl-10 pr-3 py-3 text-sm font-medium text-sprouts-text focus:outline-none focus:ring-2 focus:ring-sprouts-primary/40"
                autoFocus
              />
            </div>
            <div className="overflow-y-auto flex-1 -mx-5 sm:-mx-6 px-5 sm:px-6">
              {filteredSchools.length === 0 ? (
                <p className="text-center text-sprouts-muted py-10 text-sm">
                  {schools.length === 0
                    ? "No approved schools yet. Please ask your school to register."
                    : "No matches. Try a different name."}
                </p>
              ) : (
                filteredSchools.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      set("school_id", s.id);
                      setPickerOpen(false);
                      setSchoolSearch("");
                    }}
                    data-testid={`picker-row-${s.id}`}
                    className="w-full flex items-center gap-3 py-3 border-b border-sprouts-border last:border-0 text-left hover:bg-slate-50 px-2 -mx-2 rounded-lg"
                  >
                    <div className="w-9 h-9 rounded-full bg-sprouts-lavender flex items-center justify-center shrink-0">
                      <SchoolIcon size={18} className="text-sprouts-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-extrabold text-sm text-sprouts-text truncate">{s.school_name}</p>
                      <p className="text-xs text-sprouts-muted mt-0.5">
                        {s.city}
                        {s.state ? `, ${s.state}` : ""}
                      </p>
                    </div>
                    {form.school_id === s.id && (
                      <CheckCircle2 size={20} className="text-sprouts-success" />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
